import { useEffect, useRef, useState } from 'react';

export default function ContactSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('organization', formData.organization);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6mhcsaqoe30lj0v8bt0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', organization: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="py-16 sm:py-24 md:py-32 px-4 sm:px-8 bg-white"
    >
      <div className="max-w-4xl mx-auto">
        <div
          className={`text-center mb-10 sm:mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="font-sans text-3xl sm:text-4xl md:text-5xl font-bold text-sumi mb-3 sm:mb-4 tracking-wide">
            Contact
          </h2>
          <p className="font-sans text-sm sm:text-lg text-sumi/70">
            Get in touch with us for inquiries and consultations
          </p>
        </div>

        <div
          className={`transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <form
            id="contact-form-en"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-washi rounded-lg p-6 sm:p-10 space-y-5 sm:space-y-6"
          >
            <div>
              <label htmlFor="name" className="block font-sans text-sm font-medium text-sumi mb-2">
                Name <span className="text-indigo">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-md border border-sumi/20 bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-indigo/50 transition-all"
              />
            </div>

            <div>
              <label htmlFor="email" className="block font-sans text-sm font-medium text-sumi mb-2">
                Email Address <span className="text-indigo">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-md border border-sumi/20 bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-indigo/50 transition-all"
              />
            </div>

            <div>
              <label htmlFor="organization" className="block font-sans text-sm font-medium text-sumi mb-2">
                Organization
              </label>
              <input
                type="text"
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-md border border-sumi/20 bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-indigo/50 transition-all"
              />
            </div>

            <div>
              <label htmlFor="message" className="block font-sans text-sm font-medium text-sumi mb-2">
                Message <span className="text-indigo">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 rounded-md border border-sumi/20 bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-indigo/50 transition-all resize-none"
              ></textarea>
              <p className="mt-2 text-xs text-sumi/60 font-sans">
                {formData.message.length} / 500 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-indigo text-white font-sans text-sm font-medium py-4 rounded-md hover:bg-indigo/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {submitStatus === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800 font-sans text-center">
                  Thank you for your inquiry. We have received your message.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800 font-sans text-center">
                  Failed to send. Please try again.
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}